import type { AnalysisBuffer } from '../types/image.js';
import type { CompressionMetrics } from '../types/metrics.js';

/**
 * JPEG artefact heuristics.
 *
 * Both metrics need the original pixel grid: the 8x8 DCT blocks of a JPEG only
 * line up with the image when no resampling happened. On a downscaled analysis
 * buffer the numbers would be meaningless, so the metric explicitly reports
 * `measured: false` and the check downgrades to `not_checked`.
 */

const GRID = 8;

export interface CompressionOptions {
  /** Estimated sensor noise; used as the tolerance of the monotonicity test. */
  noiseSigma?: number;
}

export const computeCompression = (
  buffer: AnalysisBuffer,
  options: CompressionOptions = {},
): CompressionMetrics => {
  if (buffer.scale < 0.99) {
    return { measured: false, blockiness: 0, ringing: 0 };
  }
  const { luma, width, height } = buffer;
  if (width < GRID * 2 || height < GRID * 2) {
    return { measured: false, blockiness: 0, ringing: 0 };
  }

  let boundarySum = 0;
  let boundaryCount = 0;
  let interiorSum = 0;
  let interiorCount = 0;

  for (let y = 1; y < height; y += 1) {
    for (let x = 1; x < width; x += 1) {
      const difference = Math.abs((luma[y * width + x] as number) - (luma[y * width + x - 1] as number));
      if (x % GRID === 0) {
        boundarySum += difference;
        boundaryCount += 1;
      } else {
        interiorSum += difference;
        interiorCount += 1;
      }
    }
  }
  for (let x = 1; x < width; x += 1) {
    for (let y = 1; y < height; y += 1) {
      const difference = Math.abs((luma[y * width + x] as number) - (luma[(y - 1) * width + x] as number));
      if (y % GRID === 0) {
        boundarySum += difference;
        boundaryCount += 1;
      } else {
        interiorSum += difference;
        interiorCount += 1;
      }
    }
  }

  const boundaryMean = boundaryCount > 0 ? boundarySum / boundaryCount : 0;
  const interiorMean = interiorCount > 0 ? interiorSum / interiorCount : 0;
  const blockiness =
    boundaryMean + interiorMean > 1e-6
      ? Math.max(0, Math.min(1, (boundaryMean - interiorMean) / (boundaryMean + interiorMean)))
      : 0;

  /*
   * Ringing = the intensity profile across an edge is *not* monotone, which is
   * exactly what JPEG's DCT basis functions produce next to a hard edge. A clean
   * (even very sharp) edge is monotone and must not be counted, so the check
   * looks at the second neighbour on both sides of the edge.
   */
  let edgePixels = 0;
  let overshootPixels = 0;
  /**
   * Sensor noise makes a clean edge non-monotone by a few levels; that is not
   * ringing. JPEG ringing overshoots by much more than the noise floor.
   */
  const tolerance = Math.max(4, (options.noiseSigma ?? 2) * 1.5);
  const monotone = (a: number, b: number, c: number, d: number): boolean =>
    (a <= b + tolerance && b <= c + tolerance && c <= d + tolerance) ||
    (a >= b - tolerance && b >= c - tolerance && c >= d - tolerance);

  for (let y = 2; y < height - 2; y += 1) {
    for (let x = 2; x < width - 2; x += 1) {
      const left2 = luma[y * width + x - 2] as number;
      const left1 = luma[y * width + x - 1] as number;
      const right1 = luma[y * width + x + 1] as number;
      const right2 = luma[y * width + x + 2] as number;
      const gradient = Math.abs(right1 - left1);
      if (gradient >= 60) {
        edgePixels += 1;
        if (!monotone(left2, left1, right1, right2)) overshootPixels += 1;
      }
      const up2 = luma[(y - 2) * width + x] as number;
      const up1 = luma[(y - 1) * width + x] as number;
      const down1 = luma[(y + 1) * width + x] as number;
      const down2 = luma[(y + 2) * width + x] as number;
      const verticalGradient = Math.abs(down1 - up1);
      if (verticalGradient >= 60) {
        edgePixels += 1;
        if (!monotone(up2, up1, down1, down2)) overshootPixels += 1;
      }
    }
  }

  return {
    measured: true,
    blockiness,
    ringing: edgePixels > 0 ? overshootPixels / edgePixels : 0,
  };
};
